import { Switch, Route, Router as WouterRouter, useLocation } from "wouter";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from "react-helmet-async";
import { useEffect, useState } from "react";
import { ChevronUp } from "lucide-react";

// Pages
import Install from "@/pages/Install";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Gallery from "@/pages/Gallery";
import Contact from "@/pages/Contact";
import Join from "@/pages/Join";
import JoinAthlete from "@/pages/JoinAthlete";
import JoinCoach from "@/pages/JoinCoach";
import FootballAcademy from "@/pages/services/FootballAcademy";
import TalentScouting from "@/pages/services/TalentScouting";
import PlayerRepresentation from "@/pages/services/PlayerRepresentation";
import InternationalTransfers from "@/pages/services/InternationalTransfers";
import CareerInvestment from "@/pages/services/CareerInvestment";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import TermsOfService from "@/pages/TermsOfService";
import Partner from "@/pages/Partner";
import Athletes from "@/pages/Athletes";
import Team from "@/pages/Team";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import NotFound from "@/pages/not-found";

// Layout
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { NewsletterBand } from "@/components/NewsletterBand";
import { ChatWidget } from "@/components/ChatWidget";
import { NewsletterPopup } from "@/components/NewsletterPopup";
import { PWAInstallBanner } from "@/components/PWAInstallBanner";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function ScrollToTop() {
  const [location] = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location]);
  return null;
}

function BackToTopButton() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: "smooth" });

  if (!visible) return null;

  return (
    <button
      onClick={scrollToTop}
      aria-label="Back to top"
      className="fixed bottom-8 right-8 z-50 w-12 h-12 rounded-full bg-primary text-primary-foreground shadow-lg shadow-primary/30 flex items-center justify-center hover:bg-primary/90 hover:scale-110 active:scale-95 transition-all duration-200"
    >
      <ChevronUp size={22} strokeWidth={2.5} />
    </button>
  );
}

function SiteLayout() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <main className="flex-grow">
        <Switch>
          <Route path="/" component={Home} />
          <Route path="/about" component={About} />
          <Route path="/services" component={Services} />
          <Route path="/gallery" component={Gallery} />
          <Route path="/contact" component={Contact} />
          <Route path="/join" component={Join} />
          <Route path="/join/athlete" component={JoinAthlete} />
          <Route path="/join/coach" component={JoinCoach} />

          <Route path="/services/football-academy" component={FootballAcademy} />
          <Route path="/services/talent-scouting" component={TalentScouting} />
          <Route path="/services/player-representation" component={PlayerRepresentation} />
          <Route path="/services/international-transfers" component={InternationalTransfers} />
          <Route path="/services/career-investment" component={CareerInvestment} />

          <Route path="/athletes" component={Athletes} />
          <Route path="/team" component={Team} />
          <Route path="/blog" component={Blog} />
          <Route path="/blog/:slug" component={BlogPost} />

          <Route path="/scouts" component={Partner} />
          <Route path="/track" component={Contact} />

          <Route path="/partner" component={Partner} />
          <Route path="/privacy-policy" component={PrivacyPolicy} />
          <Route path="/terms-of-service" component={TermsOfService} />

          <Route component={NotFound} />
        </Switch>
      </main>
      <NewsletterBand />
      <Footer />
      <ChatWidget />
      <NewsletterPopup />
      <PWAInstallBanner />
      <BackToTopButton />
    </div>
  );
}

function Router() {
  return (
    <>
      <ScrollToTop />
      <Switch>
        <Route path="/install" component={Install} />
        <Route component={SiteLayout} />
      </Switch>
    </>
  );
}

function App() {
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <WouterRouter>
            <Router />
          </WouterRouter>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

export default App;
